import React, { ComponentType, ReactNode } from 'react';
import compose from 'lodash/fp/compose';
import { useQuery } from '@apollo/client';
import { useFreightLasa } from '@catalogo/service-americanas-product/src/hooks/freight-lasa';
import { withCookies } from '@catalogo/core-connect/cookies';
import { withDevice } from '@catalogo/core-connect/device';
import { withMetrics } from '@catalogo/core-metrics';
import { withProductQuantity } from '@catalogo/service-americanas-product/src/hocs/product-quantity';
import { setInitialStore, fmtNearFreightOptions } from '@catalogo/service-americanas-product/src/helpers/near-store';
import {
  getNearStoreServiceAcom,
  getSelectedStore,
} from '@catalogo/service-americanas-product/src/queries/store.graphql';

type Result = {
  sku: string;
  seller: {
    id: string;
  };
};

interface WithNearStoreProps {
  children: ReactNode;
  cookies: {
    persistentCep: string;
  };
  currentSku: {
    offers: {
      result: Result[];
    };
  };
  productQty: number;
  dispatchMetrics(): void;
}

export const withNearStore = (Component: ComponentType) => (props: WithNearStoreProps) => {
  const { cookies, currentSku, dispatchMetrics, productQty } = props;
  const { persistentCep } = cookies;
  const offer = currentSku?.offers?.result?.[0];
  const sellerId = offer?.seller?.id;
  const skuId = offer?.sku;

  const { data: storeData, loading: storeLoading, error: storeError } = useQuery(getNearStoreServiceAcom, {
    variables: {
      cep: persistentCep,
      radius: 35,
      skuId,
      sellerId,
    },
    skip: !persistentCep || !skuId || !sellerId,
    ssr: false,
    onCompleted: setInitialStore,
    fetchPolicy: 'network-only',
    onError: setInitialStore,
  });

  const { data } = useQuery(getSelectedStore);
  const store = data?.selectedStore;

  const { loading: freightLoading, data: freightData, previousData: previousFreightData } = useFreightLasa({
    persistentCep,
    offer,
    dispatchMetrics,
    currentStore: store,
    productQty,
  });

  const isLoading = freightLoading || storeLoading;
  const currentFreight = freightData?.freight ?? previousFreightData?.freight;
  const fmtFreightData = {
    freight: {
      ...currentFreight,
      options: fmtNearFreightOptions(currentFreight),
      hasFreightIncentive:
        currentFreight?.options?.[0]?.products?.[0]?.freightIncetive?.code === 'MORE_UNIT_FOR_CHEAPER_FREIGHT',
    },
  };

  const nearStoreProps = {
    store,
    storeData,
    storeError,
    isLoading,
    showLoader: isLoading && !currentFreight?.options,
    freightData: fmtFreightData,
    errorData: freightData?.freight?.error,
    isInvalidCep: freightData?.freight?.error?.errorCode === 'INVALID_CEP',
  };

  return <Component {...nearStoreProps} {...props} />;
};

export default compose(withCookies, withDevice, withMetrics, withProductQuantity, withNearStore);
